/**
 * brand.js
 *
 * The ethy brand mark is always written in lowercase — including at the start of
 * a sentence, a heading or a page title.
 *
 * Copy reaches the UI from three places: the built-in microcopy (en.json), the
 * hosted translation.json edited in Console, and `marketplaceName` (which comes
 * from the REACT_APP_MARKETPLACE_NAME env var and is interpolated into a large
 * number of messages). Rather than trusting each of those to get the casing
 * right, the brand name is normalised centrally: messages are normalised where
 * they are merged in app.js, and marketplaceName where configs are merged in
 * util/configHelpers.js.
 */

export const BRAND_NAME = 'ethy';

// Matches the brand as a whole word only, so "Ethyl" or "navEthySite" are left
// alone. Case-insensitive, so "Ethy" and "ETHY" are both normalised.
const BRAND_WORD_REGEXP = /\bethy\b/gi;

/**
 * Lowercase the brand name in a string.
 *
 * @param {*} value any value; non-strings are returned as they are
 * @returns {*} the value with the brand name lowercased
 */
export const lowercaseBrandName = value =>
  typeof value === 'string' ? value.replace(BRAND_WORD_REGEXP, BRAND_NAME) : value;

/**
 * Lowercase the brand name in every message of a translation map.
 * Keys are left untouched — only the copy shown to people is normalised.
 *
 * @param {Object} messages map of translation key to message
 * @returns {Object} map with the brand name lowercased in each message
 */
export const lowercaseBrandNameInMessages = messages =>
  Object.fromEntries(
    Object.entries(messages || {}).map(([key, message]) => [key, lowercaseBrandName(message)])
  );
